/**
 * Upload de arquivo (vídeo/imagem) para o backend, que repassa ao Bunny.
 *
 * ⚠️ POR QUE XMLHttpRequest E NÃO `fetch`: o `fetch` não reporta progresso de UPLOAD
 * (só de download). Um vídeo tem centenas de MB e leva minutos — sem a barra, a pessoa
 * acha que travou, fecha a aba, e o upload morre pela metade. O progresso aqui não é
 * enfeite: é o que faz a pessoa esperar.
 *
 * Vivia dentro de `Components/ContentForm/ContentForm.jsx` (painel antigo). Foi extraído
 * para cá quando o painel novo passou a precisar do mesmo comportamento — os dois usam
 * ESTA função, então corrigir um bug aqui corrige nos dois.
 *
 * Retorno (o backend devolve o JSON do BunnyService):
 *   UPLOADS.video()     -> { videoId, embedUrl, thumbnailUrl }
 *   UPLOADS.thumbnail() -> { url }
 *
 * @param {string}   endpoint     URL do endpoint (ver API_ROUTES.UPLOADS).
 * @param {File}     file         O arquivo escolhido (vai no campo "file").
 * @param {object}   extraFields  Campos extras do multipart (ex.: { title }).
 * @param {function} onProgress   Recebe 0..100 conforme os bytes sobem.
 */
export const uploadToBunny = (endpoint, file, extraFields, onProgress) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem("token");
    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    // ⚠️ NÃO definir Content-Type: com FormData o navegador precisa montar o
    // `multipart/form-data; boundary=...` sozinho. Fixando o header, o boundary some
    // e o multer do backend não acha o arquivo.

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error("Resposta inválida do servidor"));
        }
      } else {
        reject(new Error(mensagemDeErro(xhr)));
      }
    };

    xhr.onerror = () => reject(new Error("Erro de rede no upload"));

    const fd = new FormData();
    fd.append("file", file);
    Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
    xhr.send(fd);
  });

/**
 * "Falha no upload (HTTP 413)" não diz à pessoa o que fazer. O backend manda o motivo
 * em `message` ("Arquivo de vídeo ausente", limite do multer...) — é isso que ajuda.
 * O HTTP cru fica como último recurso, e os códigos que têm causa conhecida ganham
 * texto próprio.
 */
function mensagemDeErro(xhr) {
  try {
    const corpo = JSON.parse(xhr.responseText);
    const m = corpo?.message;
    if (typeof m === "string" && m) return m;
    if (Array.isArray(m) && m.length) {
      return m.map((x) => x?.message || x).filter(Boolean).join(" · ");
    }
  } catch {
    /* resposta sem corpo JSON (ex.: erro do proxy) — cai nos casos abaixo */
  }

  if (xhr.status === 401 || xhr.status === 403)
    return "Acesso negado. Faça login de novo como administrador.";
  if (xhr.status === 413)
    return "Arquivo grande demais. O limite é 15 MB para imagem e 2 GB para vídeo.";
  if (xhr.status === 0)
    return "O upload foi interrompido — verifique a conexão e tente de novo.";
  return `Falha no upload (HTTP ${xhr.status})`;
}

export default uploadToBunny;
